/**
 * Top navigation bar.
 * Shows the OMEN logo, flight selection trigger, settings trigger,
 * the current user's name, and a logout button.
 * No scrolling allowed inside the navbar.
 *
 * @param onOpenSelection - callback to open the SelectionModal
 * @param onOpenSettings  - callback to open the SettingsModal
 */

import axios from 'axios';
import { useAppStore } from '../store/appStore';
import { IconMapping } from '../constants/iconMapping';
import styles from './Navbar.module.css';

interface NavbarProps {
  onOpenSelection: () => void;
  onOpenSettings: () => void;
  /** If defined, renders the Tetris trigger button just before the user icon. */
  onOpenTetris?: () => void;
}

/** Saves Tetris score silently before logging out. Errors are swallowed. */
async function saveTetrisScore(token: string) {
  try {
    void token;
  } catch {
    // swallowed intentionally
  }
}

/**
 * Application top bar.
 */
export default function Navbar({ onOpenSelection, onOpenSettings, onOpenTetris }: NavbarProps) {
  const user       = useAppStore((s) => s.user);
  const token      = useAppStore((s) => s.token);
  const logout     = useAppStore((s) => s.logout);
  const isTourDemo = useAppStore((s) => s.isTourDemo);

  const SelectionIcon = IconMapping.navbar_selection;
  const SettingsIcon  = IconMapping.navbar_settings;
  const LogoutIcon    = IconMapping.navbar_logout;
  const UserIcon      = IconMapping.navbar_user;

  const handleLogout = async () => {
    if (token) await saveTetrisScore(token);
    logout();
    window.location.replace('/tracker/login');
  };

  /** Inline style applied to buttons that are disabled in demo mode. */
  const demoDisabledStyle = isTourDemo
    ? { opacity: 0.3, cursor: 'not-allowed', pointerEvents: 'none' as const }
    : undefined;

  return (
    <nav className={styles.navbar}>
      <div className={styles.left}>
        <img
          src="/tracker/logo.png"
          alt="OMEN"
          className={styles.logo}
          width={32}
          height={32}
        />
        <span className={styles.appName}>OMEN</span>
      </div>

      <div className={styles.actions}>
        <button
          className={styles.iconBtn}
          onClick={onOpenSelection}
          title="Sélection des vols"
          aria-label="Sélection des vols"
          data-tour="tour-flight-selector-button"
        >
          <SelectionIcon size={18} />
          <span className={styles.btnLabel}>Sélection</span>
        </button>

        <button
          className={styles.iconBtn}
          onClick={onOpenSettings}
          title="Paramètres"
          aria-label="Paramètres"
          data-tour="tour-settings-open-button"
        >
          <SettingsIcon size={18} />
          <span className={styles.btnLabel}>Paramètres</span>
        </button>
      </div>

      <div className={styles.right}>
        {onOpenTetris && (
          <button
            className={styles.tetrisBtn}
            onClick={onOpenTetris}
            title="Tetris"
            aria-label="Tetris"
            disabled={isTourDemo}
            style={demoDisabledStyle}
          >
            {/* T-tetromino SVG */}
            <svg width="18" height="14" viewBox="0 0 18 14" fill="currentColor" aria-hidden="true">
              <rect x="0" y="7" width="6" height="6" rx="1" />
              <rect x="6" y="7" width="6" height="6" rx="1" />
              <rect x="12" y="7" width="6" height="6" rx="1" />
              <rect x="6" y="0" width="6" height="6" rx="1" />
            </svg>
          </button>
        )}

        <UserIcon size={15} className={styles.userIcon} />
        <span className={styles.username} title={user?.name ?? ''}>
          {user?.name ?? '—'}
        </span>

        <button
          className={styles.iconBtn}
          onClick={handleLogout}
          title="Déconnexion"
          aria-label="Déconnexion"
          disabled={isTourDemo}
          style={demoDisabledStyle}
        >
          <LogoutIcon size={18} />
        </button>
      </div>
    </nav>
  );
}

useAppStore.subscribe((state) => {
  if (state.token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
});
